import { artistHref } from '@/features/artists/artistHref'
import { sharePlaylist, saveAndShare } from '@/features/sharing/ShareSheet'
import { CollectionHeader, pageClassName } from '@/components/ui/Page'
import { ErrorNotice } from '@/components/ui/Feedback'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowDownToLine,
  ArrowLeft,
  ArrowUp,
  ListMusic,
  ListX,
  MoreHorizontal,
  Pencil,
  Play,
  Search,
  Shuffle,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { api, ApiError } from '@/api/client'
import { isIncognito } from '@/lib/incognito'
import {
  useRequestTrack,
  useToggleFavorite,
  useDeletePlaylist,
  useJobs,
  usePlaylist,
  useRemoveFromPlaylist,
  useReorderPlaylist,
} from '@/api/hooks'
import type {
  AddTrackResponse,
  DownloadJob,
  LibraryItem,
  LibraryStatus,
  Paginated,
  PlaylistEntry,
  Track,
} from '@/api/types'
import { usePendingPlayback } from '@/audio/pendingPlayback'
import { toQueueItem, usePlayer, type QueueItem } from '@/audio/store'
import { songActions } from '@/components/songActions'
import { useHomePlayback } from '@/features/home/useHomePlayback'
import { AddToPlaylistSheet } from '@/components/selection/AddToPlaylistSheet'
import { TrackRow } from '@/components/TrackRow'
import {
  Button,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  Skeleton,
  Spinner,
  toast,
  TrackListSkeleton,
} from '@/components/ui'

import { describeError, totalDurationLabel, trackCountLabel } from './helpers'
import { PlaylistFormDialog } from './PlaylistFormDialog'
import { PlaylistMosaic } from './PlaylistMosaic'

const PAGE = pageClassName('collection')

/** How many library requests to have in flight while materialising a playlist. */
const RESOLVE_CONCURRENCY = 4

/** The library endpoint's `max_page_size`. One request buys us this many rows. */
const INDEX_PAGE_SIZE = 200

/**
 * A one-request snapshot of the library, used purely to tell which of a
 * playlist's tracks are already on disk.
 *
 * Playlist entries are catalog tracks and carry no per-user library status, so
 * there is nothing on the playlist payload to read. `complete` is false when the
 * library does not fit in a single page — in that case a track's absence from
 * the snapshot proves nothing, and the UI stays quiet rather than claiming a
 * song is missing when it is not.
 */
function useLibraryIndex() {
  const { data } = useQuery({
    // Sharing the `library` prefix means `useRequestTrack` refreshes this for free.
    queryKey: ['library', 'playlist-index'],
    queryFn: () =>
      api.get<Paginated<LibraryItem>>('/library/tracks', { page_size: INDEX_PAGE_SIZE }),
    staleTime: 60_000,
  })

  return useMemo(
    () => ({
      byTrackId: new Map((data?.results ?? []).map((item) => [item.track.id, item] as const)),
      complete: data ? data.next === null : false,
    }),
    [data],
  )
}

/**
 * Turn catalog tracks into this user's library items, a few at a time.
 *
 * This deliberately bypasses `useRequestTrack` and talks to the client directly: the
 * hook invalidates three query keys on every success, which for a 200-track
 * playlist is 200 rounds of refetching. The caller invalidates once at the end
 * instead. A track the provider cannot serve is skipped rather than aborting
 * the rest of the playlist.
 */
async function resolveTracks(
  tracks: Track[],
  onResolved: (track: Track, item: LibraryItem) => void,
): Promise<void> {
  let cursor = 0

  async function worker(): Promise<void> {
    while (cursor < tracks.length) {
      const track = tracks[cursor]
      cursor += 1
      try {
        const response = await api.post<AddTrackResponse>('/playback/tracks', {
          provider: track.provider,
          external_id: track.external_id,
          incognito: isIncognito(),
        })
        onResolved(track, response.library_item)
      } catch {
        // Skipped. The queue is built from whatever did resolve.
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(RESOLVE_CONCURRENCY, tracks.length) }, () => worker()),
  )
}

interface RowState {
  status?: LibraryStatus
  progress?: number
  /** Known to have no library item yet. Unknown is not the same as absent. */
  notDownloaded: boolean
}

function DetailSkeleton() {
  return (
    <div className={PAGE}>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
        <Skeleton className="aspect-square w-36 self-center rounded-card sm:w-48 sm:self-auto" />
        <div className="min-w-0 flex-1 space-y-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-7 w-2/3 max-w-80" />
          <Skeleton className="h-3.5 w-1/3 max-w-48" />
        </div>
      </div>
      <div className="mt-5 flex gap-2">
        <Skeleton className="h-11 w-28 rounded-lg" />
        <Skeleton className="h-11 w-28 rounded-lg" />
        <Skeleton className="h-11 w-36 rounded-lg" />
      </div>
      <div className="mt-6">
        <TrackListSkeleton count={8} />
      </div>
    </div>
  )
}

export function PlaylistDetailPage() {
  const params = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const parsedId = Number(params.id)
  const playlistId = Number.isInteger(parsedId) && parsedId > 0 ? parsedId : undefined

  const { data: playlist, isPending, isError, error, refetch, isFetching } = usePlaylist(playlistId)

  const libraryIndex = useLibraryIndex()
  const { data: jobsPage } = useJobs('active')

  const playback = useHomePlayback('playlist')
  const favorite = useToggleFavorite()
  const [playlistTrack, setPlaylistTrack] = useState<number | null>(null)
  const addTrack = useRequestTrack()
  const reorder = useReorderPlaylist()
  const removeEntryMutation = useRemoveFromPlaylist()
  const deletePlaylist = useDeletePlaylist()

  const playerTrackId = usePlayer((state) => {
    const item = state.current()
    return item ? item.trackId : null
  })
  const playerIsPlaying = usePlayer((state) => state.isPlaying)

  /** Library items learned during this visit, from playing or downloading. */
  const [resolved, setResolved] = useState<ReadonlyMap<number, LibraryItem>>(() => new Map())
  const [starting, setStarting] = useState<'play' | 'shuffle' | null>(null)
  const [resolvingTrackId, setResolvingTrackId] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const activeJobByTrackId = useMemo(() => {
    const map = new Map<number, DownloadJob>()
    for (const job of jobsPage?.results ?? []) {
      if (job.status === 'queued' || job.status === 'running') map.set(job.track.id, job)
    }
    return map
  }, [jobsPage])

  const repairDownloads = useMutation({
    mutationFn: (id: number) => api.post<{ queued: number }>(`/playlists/${id}/download`),
    onMutate: () => setActionError(null),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['library'] })
      void queryClient.invalidateQueries({ queryKey: ['jobs'] })
      if (result.queued === 0) {
        toast.info('Downloads are up to date')
      } else {
        toast.success(
          `Queued ${result.queued} ${result.queued === 1 ? 'download' : 'downloads'}`,
          'Follow progress in Downloads.',
        )
      }
    },
    onError: (mutationError) =>
      setActionError(describeError(mutationError, 'Could not queue those downloads. Try again.')),
  })

  function itemForTrack(trackId: number): LibraryItem | undefined {
    return resolved.get(trackId) ?? libraryIndex.byTrackId.get(trackId)
  }

  function rowState(trackId: number): RowState {
    const job = activeJobByTrackId.get(trackId)
    if (job) {
      return {
        status: job.status === 'queued' ? 'pending' : 'downloading',
        progress: job.progress,
        notDownloaded: false,
      }
    }

    const item = itemForTrack(trackId)
    if (item) {
      return {
        status: item.status === 'ready' ? undefined : item.status,
        notDownloaded: false,
      }
    }

    return { notDownloaded: libraryIndex.complete }
  }

  function rememberItems(items: Iterable<readonly [number, LibraryItem]>) {
    setResolved((previous) => {
      const next = new Map(previous)
      for (const [trackId, item] of items) next.set(trackId, item)
      return next
    })
  }

  async function startPlaylist(shuffle: boolean) {
    if (!playlist || playlist.entries.length === 0) return

    setActionError(null)
    setStarting(shuffle ? 'shuffle' : 'play')

    try {
      const items = new Map<number, LibraryItem>()
      for (const entry of playlist.entries) {
        const known = itemForTrack(entry.track.id)
        if (known?.stream_url) items.set(entry.track.id, known)
      }

      // Duplicates are legal in a playlist; ask about each track once.
      const missing = [
        ...new Map(
          playlist.entries
            .filter((entry) => !items.has(entry.track.id))
            .map((entry) => [entry.track.id, entry.track] as const),
        ).values(),
      ]

      if (missing.length > 0) {
        await resolveTracks(missing, (track, item) => items.set(track.id, item))
        rememberItems(items)
        void queryClient.invalidateQueries({ queryKey: ['library'] })
        void queryClient.invalidateQueries({ queryKey: ['jobs'] })
      }

      const queue: QueueItem[] = []
      for (const entry of playlist.entries) {
        const item = items.get(entry.track.id)
        const queueItem = item ? toQueueItem(item) : null
        if (queueItem) queue.push(queueItem)
      }

      if (queue.length === 0) {
        setActionError(
          'Nothing here is downloaded yet. The songs have been queued — press play again once they finish.',
        )
        return
      }

      const player = usePlayer.getState()
      if (shuffle && !player.shuffle) player.toggleShuffle()
      // Nobody picked a starting track here — the 0 is just "the top of the
      // playlist" — so nothing gets pinned and a shuffle really starts anywhere.
      // Unconditional because the option is ignored unless shuffle is on, and
      // pressing plain Play with shuffle already enabled is the same situation.
      player.playQueue(queue, 0, { startFrom: 'anywhere', source: 'playlist' })

      const skipped = playlist.entries.length - queue.length
      if (skipped > 0) {
        toast.info(
          `Playing ${queue.length} of ${playlist.entries.length}`,
          `${skipped === 1 ? '1 song is' : `${skipped} songs are`} still downloading and will be skipped for now.`,
        )
      }
    } catch (thrown) {
      setActionError(describeError(thrown, 'Could not start this playlist. Try again.'))
    } finally {
      setStarting(null)
    }
  }

  /**
   * Playlist entries are catalog tracks, so a row may point at something this
   * user has never downloaded. Adding is idempotent and returns the library item,
   * so it doubles as "resolve this row".
   */
  async function playEntry(entry: PlaylistEntry) {
    if (!playlist) return

    setActionError(null)
    setResolvingTrackId(entry.track.id)

    try {
      const { library_item: item } = await addTrack.mutateAsync({
        provider: entry.track.provider,
        external_id: entry.track.external_id,
      })
      rememberItems([[entry.track.id, item] as const])

      if (!toQueueItem(item)) {
        // Not on disk yet. Hand the intent to the pending-playback layer so it
        // starts by itself when the download lands, rather than asking the user
        // to come back and press play again.
        usePendingPlayback.getState().requestWhenReady(item.id, 'play', 'playlist')
        toast.info(
          `Downloading “${entry.track.title}”`,
          'It will start playing when it is ready.',
        )
        return
      }

      // Continue through the rest of the playlist wherever we already have files,
      // so pressing one row does not strand the player on a queue of one.
      const queue: QueueItem[] = []
      let startIndex = 0
      for (const other of playlist.entries) {
        const otherItem = other.track.id === entry.track.id ? item : itemForTrack(other.track.id)
        const queueItem = otherItem ? toQueueItem(otherItem) : null
        if (!queueItem) continue
        if (other.id === entry.id) startIndex = queue.length
        queue.push(queueItem)
      }

      usePlayer.getState().playQueue(queue, startIndex, { source: 'playlist' })
    } catch (thrown) {
      setActionError(describeError(thrown, `Could not play “${entry.track.title}”.`))
    } finally {
      setResolvingTrackId(null)
    }
  }

  function moveEntry(from: number, delta: number) {
    if (!playlist) return
    const to = from + delta
    if (to < 0 || to >= playlist.entries.length) return

    const entry = playlist.entries[from]
    const total = playlist.entries.length
    setActionError(null)
    reorder.mutate(
      { playlistId: playlist.id, entryId: entry.id, toPosition: to },
      {
        onSuccess: () =>
          setAnnouncement(`${entry.track.title} moved to position ${to + 1} of ${total}.`),
        onError: (mutationError) =>
          setActionError(describeError(mutationError, 'Could not reorder the playlist. Try again.')),
      },
    )
  }

  function removeEntry(entry: PlaylistEntry) {
    if (!playlist) return
    setActionError(null)
    removeEntryMutation.mutate(
      { playlistId: playlist.id, entryId: entry.id },
      {
        onSuccess: () => setAnnouncement(`${entry.track.title} removed from the playlist.`),
        onError: (mutationError) =>
          setActionError(describeError(mutationError, 'Could not remove that song. Try again.')),
      },
    )
  }

  function confirmDelete() {
    if (!playlist) return
    const name = playlist.name
    setDeleteError(null)
    deletePlaylist.mutate(playlist.id, {
      onSuccess: () => {
        setDeleteOpen(false)
        toast.success('Playlist deleted', `“${name}” is gone. Your songs are untouched.`)
        void navigate('/playlists', { replace: true })
      },
      onError: (mutationError) =>
        setDeleteError(
          describeError(mutationError, 'Could not delete that playlist. Please try again.'),
        ),
    })
  }

  const isMissing =
    playlistId === undefined || (isError && error instanceof ApiError && error.status === 404)

  if (isMissing) {
    return (
      <div className={PAGE}>
        <EmptyState
          icon={ListMusic}
          title="Playlist not found"
          description="It may have been deleted, or the link points somewhere that no longer exists."
          action={{
            label: 'Back to playlists',
            onClick: () => void navigate('/playlists'),
            icon: ArrowLeft,
          }}
        />
      </div>
    )
  }

  if (isError) {
    return (
      <div className={PAGE}>
        <ErrorNotice title="Could not load this playlist" busy={isFetching} onRetry={() => void refetch()}>
          {describeError(error, 'Check your connection and try again.')}
        </ErrorNotice>
      </div>
    )
  }

  if (isPending || playlist === undefined) return <DetailSkeleton />

  const entries = playlist.entries
  const needsRepair = entries.some((entry) => {
    const state = rowState(entry.track.id)
    return state.notDownloaded || state.status === 'failed' || state.status === 'missing'
  })
  const totalMs = entries.reduce((sum, entry) => sum + entry.track.duration_ms, 0)
  const busy = starting !== null

  return (
    <div className={PAGE}>
      <Link
        to="/playlists"
        className="-ml-2 mb-3 inline-flex h-11 items-center gap-1.5 rounded-lg px-2 text-sm text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Playlists
      </Link>

      <CollectionHeader kind="Playlist" title={playlist.name} artwork={<PlaylistMosaic coverUrls={playlist.cover_urls} className="w-full shadow-lg" />} subtitle={playlist.description} metadata={`${trackCountLabel(entries.length)}${totalMs > 0 ? ` · ${totalDurationLabel(totalMs)}` : ''}`} />

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button
          onClick={() => void startPlaylist(false)}
          loading={starting === 'play'}
          disabled={entries.length === 0 || busy}
        >
          <Play className="h-4 w-4 fill-current" aria-hidden="true" />
          Play
        </Button>
        <Button
          variant="secondary"
          onClick={() => void startPlaylist(true)}
          loading={starting === 'shuffle'}
          disabled={entries.length === 0 || busy}
        >
          <Shuffle className="h-4 w-4" aria-hidden="true" />
          Shuffle
        </Button>
        {needsRepair ? (
          <Button
            variant="secondary"
            onClick={() => repairDownloads.mutate(playlist.id)}
            loading={repairDownloads.isPending}
          >
            <ArrowDownToLine className="h-4 w-4" aria-hidden="true" />
            Repair downloads
          </Button>
        ) : null}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton aria-label={`More options for ${playlist.name}`} variant="secondary">
              <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => sharePlaylist(playlist.id)}>Share</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => { void api.post<{ id: number }>(`/shares/duplicate/${playlist.id}`).then((copy) => { void navigate(`/playlists/${copy.id}`) }).catch((error: Error) => toast.error(error.message)) }}>Duplicate playlist</DropdownMenuItem>
            <DropdownMenuItem icon={Pencil} onSelect={() => setRenameOpen(true)}>
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem icon={Trash2} destructive onSelect={() => setDeleteOpen(true)}>
              Delete playlist
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {actionError ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <div className="mt-6">
        {entries.length === 0 ? (
          <EmptyState
            icon={ListMusic}
            title="This playlist is empty"
            description="Find songs in Search and add them here. They’re also kept in your library."
            action={{ label: 'Search for music', onClick: () => void navigate('/search'), icon: Search }}
          />
        ) : (
          <ul>
            {entries.map((entry, position) => {
              const state = rowState(entry.track.id)
              const isResolving = resolvingTrackId === entry.track.id

              return (
                <li key={entry.id}>
                  <TrackRow
                    title={entry.track.title}
                    artistName={entry.track.artist_name}
                    artistHref={artistHref(entry.track)}
                    albumName={entry.track.album_name}
                    durationMs={entry.track.duration_ms}
                    coverUrl={entry.track.artwork_url}
                    isActive={playerTrackId === entry.track.id}
                    isPlaying={playerIsPlaying}
                    status={state.status}
                    progress={state.progress}
                    onPlay={() => void playEntry(entry)}
                    isFavorite={itemForTrack(entry.track.id)?.is_favorite}
                    onToggleFavorite={itemForTrack(entry.track.id) ? () => {
                      const item = itemForTrack(entry.track.id)!
                      favorite.mutate({ id: item.id, favorite: !item.is_favorite }, {
                        onSuccess: (updated) => rememberItems([[entry.track.id, updated]]),
                        onError: (error) => toast.error('Could not update favourite', describeError(error, 'Try again.')),
                      })
                    } : undefined}
                    trailing={
                      isResolving ? (
                        <span className="px-1">
                          <Spinner size="sm" label={`Preparing ${entry.track.title}`} />
                        </span>
                      ) : state.notDownloaded ? (
                        <span
                          className="flex items-center gap-1 px-1 text-xs text-text-subtle"
                          title="Not downloaded yet"
                        >
                          <ArrowDownToLine className="h-3.5 w-3.5" aria-hidden="true" />
                          <span className="sr-only sm:not-sr-only">Not downloaded</span>
                        </span>
                      ) : undefined
                    }
                    menuItems={[
                      ...songActions({ share: () => { void saveAndShare(entry.track).catch((error: Error) => toast.error(error.message)) }, playNext: () => void playback.act(entry.track, 'next'), addToQueue: () => void playback.act(entry.track, 'queue'), addToPlaylist: () => setPlaylistTrack(entry.track.id), busy: playback.busy[`${entry.track.provider}:${entry.track.external_id}`] }),
                      {
                        label: 'Move up', context: true,
                        icon: ArrowUp,
                        disabled: position === 0 || reorder.isPending,
                        onSelect: () => moveEntry(position, -1),
                      },
                      {
                        label: 'Move down', context: true,
                        icon: ArrowDown,
                        disabled: position === entries.length - 1 || reorder.isPending,
                        onSelect: () => moveEntry(position, 1),
                      },
                      {
                        label: 'Remove from playlist',
                        icon: ListX,
                        destructive: true,
                        disabled: removeEntryMutation.isPending,
                        onSelect: () => removeEntry(entry),
                      },
                    ]}
                  />
                  {playback.errors[`${entry.track.provider}:${entry.track.external_id}`] ? <p role="alert" className="px-2 text-sm text-danger">{playback.errors[`${entry.track.provider}:${entry.track.external_id}`].message}</p> : null}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {playlistTrack != null ? <AddToPlaylistSheet open trackIds={[playlistTrack]} onOpenChange={(open) => { if (!open) setPlaylistTrack(null) }} /> : null}
      <PlaylistFormDialog open={renameOpen} onOpenChange={setRenameOpen} playlist={playlist} />

      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open && !deletePlaylist.isPending) {
            setDeleteOpen(false)
            setDeleteError(null)
          }
        }}
        title="Delete this playlist?"
        description={`“${playlist.name}” will be removed. The songs stay in your library.`}
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              disabled={deletePlaylist.isPending}
              onClick={() => setDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" loading={deletePlaylist.isPending} onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        {deleteError ? (
          <p role="alert" className="text-sm text-danger">
            {deleteError}
          </p>
        ) : null}
      </Dialog>
    </div>
  )
}
